import { useEffect, useState } from 'react'
import { ChevronRight, Zap } from 'lucide-react'
import { Shell, useShellMeta } from '../../../admin/Shell.js'
import { Select } from '../../../admin/primitives.js'
import { SegmentedControl } from '../../../admin/pickers.js'
import { Link } from '../../../router.js'
import { lireSupervision } from '../../api.js'
import { BandeauLecture, LIBELLE_GRAVITE, Pastille, TON_GRAVITE, fmtHeure, renommerSalles, useNomsSalles, useVueSonde, classeTon } from '../commun.js'
import { LIBELLE_STATUT, Quand, Retour, Squelette, Vide, donneeDe } from './composants.js'
import {
  causeEnUneLigne, cheminIncident, estDocListeIncidents, fmtDuree, tonOuverts, tonStatutIncident, type ResumeIncident,
} from './modele.js'
import { useMenuSonde } from '../menu-sonde.js'

/**
 * INCIDENTS — /infrastructure/incidents (21/10/2026).
 *
 * La liste des incidents de Sonde : en cours (ouverts, pris en charge, en
 * rétablissement) ou clos, filtrable par gravité, 50 par page. Chaque ligne
 * dit la cause en une ligne et mène à sa fiche.
 */

type Filtre = 'active' | 'resolved' | 'all'

function Ligne({ i, nomZone }: { i: ResumeIncident; nomZone: (c: string | null | undefined) => string | null }) {
  const menu = useMenuSonde()
  return (
    <li>
      <Link className={`sv-fiche-ligne-inc sv-fiche-ligne-inc--${tonStatutIncident(i.status, i.severity)}`} href={cheminIncident(i.id)}
            {...menu.incident({ id: i.id, titre: renommerSalles(i.title), status: i.status, acquitte: i.acked_at !== null, sourdine: i.snoozed_until ?? null })}>
        <span className="sv-fiche-ligne-inc__gravite"><Pastille ton={TON_GRAVITE[i.severity] ?? 'gris'}>{LIBELLE_GRAVITE[i.severity] ?? i.severity}</Pastille></span>
        <span className="sv-fiche-ligne-inc__corps">
          <span className="sv-fiche-ligne-inc__titre">{renommerSalles(i.title)}</span>
          <span className="sv-fiche-ligne-inc__meta">{causeEnUneLigne(i)}</span>
          <span className="sv-fiche-ligne-inc__meta">
            Ouvert <Quand iso={i.opened_at} /> · {i.status === 'resolved' ? 'a duré' : 'depuis'} {fmtDuree(i.duration_s)}
            {i.affected_count > 1 ? ` · ${i.affected_count} appareils` : ''}
            {i.acked_by ? ` · pris en charge par ${i.acked_by}` : ''}
            {!i.notified ? ' · aucune alerte publiée' : ''}
            {i.snoozed_until ? ` · en sourdine jusqu’à ${fmtHeure(i.snoozed_until)}` : ''}
          </span>
        </span>
        <span className="sv-fiche-ligne-inc__statut">
          <span className={classeTon(tonStatutIncident(i.status, i.severity))}>{i.status_label || LIBELLE_STATUT[i.status] || i.status}</span>
          {i.zone_code ? <span className="sv-fiche-ligne-inc__salle">{nomZone(i.zone_code)}</span> : null}
        </span>
        <ChevronRight size={16} aria-hidden="true" className="sv-fiche-app__fleche" />
      </Link>
    </li>
  )
}

export function ListeIncidentsPage() {
  const meta = useShellMeta()
  const [filtre, setFiltre] = useState<Filtre>('active')
  const [gravite, setGravite] = useState('')
  const q = `incidents?status=${filtre}&limit=50${gravite ? `&severity=${gravite}` : ''}`
  const { lecture, precedente } = useVueSonde(q, estDocListeIncidents, 20_000)
  const { data, perime } = donneeDe(lecture, precedente)
  const nomZone = useNomsSalles()
  // Heure de la dernière lecture réussie : chaque compte dit de quand il date.
  const [luA, setLuA] = useState<string | null>(null)
  useEffect(() => { if (lecture?.etat === 'ok') setLuA(new Date().toISOString()) }, [lecture])
  const [suite, setSuite] = useState<{ items: ResumeIncident[]; curseur: string | null; message: string | null }>({ items: [], curseur: null, message: null })
  const [enCours, setEnCours] = useState(false)
  useEffect(() => { setSuite({ items: [], curseur: null, message: null }) }, [q])

  const curseur = suite.curseur ?? data?.next_cursor ?? null
  const plusDisponible = suite.items.length ? suite.curseur !== null : !!data?.next_cursor
  const chargerSuite = async () => {
    if (!curseur) return
    setEnCours(true)
    const l = await lireSupervision(`/v/${q}&cursor=${encodeURIComponent(curseur)}`, estDocListeIncidents)
    setEnCours(false)
    if (l.etat !== 'ok') { setSuite((s) => ({ ...s, message: l.message })); return }
    setSuite((s) => ({ items: [...s.items, ...l.data.items], curseur: l.data.next_cursor, message: null }))
  }
  // La première page se relit seule ; les pages suivantes restent telles que chargées, sans doublon.
  const vus = new Set((data?.items ?? []).map((i) => i.id))
  const items = [...(data?.items ?? []), ...suite.items.filter((i) => !vus.has(i.id))]
  const c = data?.counts

  return (
    <Shell active="infrastructure" meta={meta}>
      <div className="lc-page lc-page--wide lc-stagger sv-fiche">
        <Retour repli="/infrastructure" libelle="Supervision" />
        <header className="sv-fiche-liste-tete">
          <div>
            <h1 className="lc-title">Incidents</h1>
            <p className="lc-subtitle">Ce que Sonde a ouvert, qui s’en occupe, et comment c’est fini. Une cause, une ligne.</p>
          </div>
        </header>
        <BandeauLecture lecture={lecture} depuis={null} />
        {c ? (
          <div className={perime ? 'sv-fiche-perime' : undefined}>
            <ul className="sv-fiche-tuiles">
              <li className={`sv-fiche-tuile sv-fiche-tuile--${perime ? 'gris' : tonOuverts(c.open, data?.items ?? [])}`}><strong>{c.open}</strong><span>ouvert{c.open > 1 ? 's' : ''}</span></li>
              <li className={`sv-fiche-tuile sv-fiche-tuile--${!perime && c.acknowledged ? 'warn' : 'gris'}`}><strong>{c.acknowledged}</strong><span>pris en charge</span></li>
              <li className={`sv-fiche-tuile sv-fiche-tuile--${!perime && c.recovering ? 'ok' : 'gris'}`}><strong>{c.recovering}</strong><span>en rétablissement</span></li>
              <li className="sv-fiche-tuile sv-fiche-tuile--gris"><strong>{c.resolved_7d}</strong><span>résolu{c.resolved_7d > 1 ? 's' : ''} en 7 jours</span></li>
            </ul>
            <p className="sv-fiche-doux">Comptes de Sonde{luA ? <> lus à <span className="sv-mono">{fmtHeure(luA)}</span></> : null}{perime ? ' — dernière lecture réussie, Sonde ne répond plus' : ''}.</p>
          </div>
        ) : null}
        <div className="sv-fiche-filtres">
          <SegmentedControl ariaLabel="Incidents affichés" value={filtre} onChange={setFiltre}
            options={[{ value: 'active', label: 'En cours' }, { value: 'resolved', label: 'Clos' }, { value: 'all', label: 'Tous' }]} />
          <Select compact ariaLabel="Gravité" value={gravite} onChange={setGravite}
            options={[{ value: '', label: 'Toutes gravités' }, ...(['critical', 'error', 'warning', 'info'] as const).map((g) => ({ value: g, label: LIBELLE_GRAVITE[g] ?? g }))]} />
        </div>
        {!data && !lecture ? <Squelette lignes={5} /> : null}
        {data ? (
          <section className={`lc-panel sv-fiche-section${perime ? ' sv-fiche-perime' : ''}`} aria-label="Liste des incidents">
            <div className="lc-panel__body">
              {items.length === 0 ? (
                <Vide>
                  <strong>{filtre === 'active' ? 'Aucun incident en cours.' : filtre === 'resolved' ? 'Aucun incident clos sur cette sélection.' : 'Aucun incident.'}</strong>
                  <span>{filtre === 'active' ? 'Sonde n’a rien ouvert : rien n’attend de geste.' : 'Changez de filtre pour voir les autres.'}</span>
                </Vide>
              ) : (
                <ul className="sv-fiche-liste">{items.map((i) => <Ligne key={i.id} i={i} nomZone={nomZone} />)}</ul>
              )}
              {suite.message ? <p className="sv-fiche-doux">{suite.message}</p> : null}
              {plusDisponible ? (
                <button type="button" className="lc-btn lc-btn--ghost sv-fiche-plus" disabled={enCours} onClick={() => { void chargerSuite() }}>
                  {enCours ? 'Chargement…' : 'Charger les incidents plus anciens'}
                </button>
              ) : null}
            </div>
          </section>
        ) : null}
        <p className="sv-fiche-pied"><Zap size={14} aria-hidden="true" /> Source : Sonde</p>
      </div>
    </Shell>
  )
}
